import React, { useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { applyReadingAction } from "../../services/workspaceService";
import { readingTabFor } from "../../config/readingStates";
import { textReaderPath } from "../../utils/textRoutes";
import { useReadingArticles } from "../../hooks/useReadingArticles";
import HomeShell, { HomePageContent } from "../shell/HomeShell";
import { BACKGROUNDS } from "../../config/backgrounds";
import deskGlass from "../../styles/deskGlass";
import ReadingCard from "./ReadingCard";
import { BookOpen, ChevronRight, Archive, Trash2 } from "lucide-react";
import "./reading.css";
import { runFeedbackFlow } from "../../utils/runFeedbackFlow";

const TABS = [
  { id: "unread", label: "Por leer" },
  { id: "later", label: "Después" },
  { id: "review", label: "Repasar" },
  { id: "read", label: "Leídos" },
  { id: "archived", label: "Archivo" },
  { id: "trash", label: "Papelera" },
];

function sortForTab(list, tab) {
  if (tab === "unread" || tab === "review") {
    return [...list].sort((a, b) => (a.updatedAt || 0) - (b.updatedAt || 0));
  }
  return [...list].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

export default function ReadingInbox() {
  const navigate = useNavigate();
  const { articles, loading, counts } = useReadingArticles();
  const [tab, setTab] = useState("unread");

  const filtered = useMemo(() => {
    const list = articles.filter((a) => readingTabFor(a) === tab);
    return sortForTab(list, tab);
  }, [articles, tab]);

  const queueForSession = useMemo(() => {
    if (tab === "unread" || tab === "later" || tab === "review") return filtered;
    return [];
  }, [filtered, tab]);

  const handleAction = useCallback(async (article, action) => {
    if (!article?.deskId) return;
    await runFeedbackFlow({
      actionId: `reading-action:${article.id}:${action}`,
      label: "Lectura",
      dbStep: async () => {
        await applyReadingAction(
          article.deskId,
          article.drawerId,
          article.notebookId,
          article.id,
          action,
        );
      },
      successMessage: "OK",
      errorMessage: "No se pudo aplicar acción",
    }).catch((err) => {
      console.error("[ReadingInbox] applyReadingAction failed:", err);
    });
  }, []);

  const openArticle = useCallback(
    (article, index) => {
      const path = textReaderPath(article);
      navigate(path, {
        state: {
          readingMode: true,
          queue: queueForSession.map((a) => a.id),
          queueIndex: index,
          returnTo: "/read",
        },
      });
    },
    [navigate, queueForSession]
  );

  const startSession = () => {
    if (queueForSession.length > 0) openArticle(queueForSession[0], 0);
  };

  return (
    <HomeShell background={BACKGROUNDS.dashboard} fullHeight={false}>
      <HomePageContent>
        <div style={{ ...deskGlass.deskContent, maxWidth: "720px", paddingBottom: "120px" }}>
          <div className="reading-inbox-header">
            <div>
              <h1 style={deskGlass.greeting}>Lectura</h1>
              <p style={deskGlass.subtext}>
                {counts.unread + counts.later + counts.review} en cola · {counts.unread} sin leer
              </p>
            </div>
            <Link to="/library" style={{ ...deskGlass.viewToggle, color: "#D4AF37", textDecoration: "none" }}>
              Biblioteca <ChevronRight size={16} />
            </Link>
          </div>

          {queueForSession.length > 0 && (
            <button type="button" className="reading-session-btn" onClick={startSession}>
              <BookOpen size={18} />
              Leer siguiente ({queueForSession.length})
            </button>
          )}

          <div className="reading-tabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                className={`reading-tab${tab === t.id ? " reading-tab--active" : ""}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
                {counts[t.id] > 0 && <span className="reading-tab__count">{counts[t.id]}</span>}
              </button>
            ))}
          </div>

          {tab === "archived" && (
            <p className="reading-hint"><Archive size={14} /> Archivados — fuera de la cola, recuperables.</p>
          )}
          {tab === "trash" && (
            <p className="reading-hint"><Trash2 size={14} /> Papelera — restaurá con «Después» o borrá en Cajones.</p>
          )}

          {loading ? (
            <div style={deskGlass.center}>Cargando…</div>
          ) : filtered.length === 0 ? (
            <div className="reading-empty">
              {tab === "unread" ? "¡Al día! No tenés lecturas pendientes." : "Nada en esta lista."}
            </div>
          ) : (
            <div className="reading-list">
              {filtered.map((art, i) => (
                <ReadingCard
                  key={art.id}
                  article={art}
                  showRestore={tab === "trash" || tab === "archived"}
                  onOpen={(a) => openArticle(a, i)}
                  onAction={handleAction}
                />
              ))}
            </div>
          )}
        </div>
      </HomePageContent>
    </HomeShell>
  );
}
